import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { ArrowLeft, GraduationCap } from "lucide-react";
import { api } from "../lib/api";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import CourseCard from "../components/CourseCard";

export default function InstructorProfile() {
  const { id } = useParams();
  const [instructor, setInstructor] = useState(null);
  const [courses, setCourses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    setLoading(true);
    setError(null);
    Promise.all([
      api.get(`/instructors/${id}`).then((r) => r.data),
      api.get(`/instructors/${id}/courses`).then((r) => r.data),
    ])
      .then(([ins, cs]) => {
        setInstructor(ins);
        setCourses(cs);
      })
      .catch((e) => {
        console.error("Failed to load instructor", e);
        setError(e.response?.status === 404 ? "Instructor not found" : "Failed to load instructor");
      })
      .finally(() => setLoading(false));
  }, [id]);

  return (
    <div className="bg-fc-navy min-h-screen text-white">
      <Navbar />
      <main className="pt-32 pb-24" data-testid="instructor-page">
        <section className="max-w-6xl mx-auto px-6 lg:px-10">
          <Link to="/courses" className="inline-flex items-center gap-2 text-white/60 hover:text-white text-xs font-bold tracking-wider mb-8">
            <ArrowLeft size={14} /> BACK TO ALL COURSES
          </Link>

          {loading ? (
            <p className="text-white/50">Loading…</p>
          ) : error || !instructor ? (
            <div className="text-center py-20">
              <h1 className="fc-h-section text-3xl mb-4">{error || "Not found"}</h1>
              <Link to="/courses" className="btn-fc-red">Browse all courses</Link>
            </div>
          ) : (
            <>
              <div className="grid md:grid-cols-[auto_1fr] gap-10 mb-16 items-start">
                <img
                  src={instructor.avatar_url}
                  alt={instructor.name}
                  className="w-44 h-44 object-cover"
                  data-testid="instructor-avatar"
                />
                <div>
                  <div className="fc-overline mb-3">INSTRUCTOR</div>
                  <h1 className="fc-h-section text-4xl sm:text-5xl mb-3" data-testid="instructor-name">{instructor.name}</h1>
                  <p className="text-fc-red text-sm tracking-[0.2em] uppercase font-bold mb-6">{instructor.title}</p>
                  <p className="text-white/80 leading-relaxed text-lg max-w-3xl mb-6" data-testid="instructor-bio">{instructor.bio}</p>

                  <div className="flex flex-wrap gap-2 mb-6">
                    {instructor.expertise?.map((e) => (
                      <span key={e} className="text-[10px] tracking-[0.18em] uppercase font-bold border border-white/15 px-3 py-1 text-white/70">
                        {e}
                      </span>
                    ))}
                  </div>

                  <div className="bg-[#060A24] border border-white/10 p-5 max-w-xl">
                    <div className="flex items-center gap-2 mb-3">
                      <GraduationCap size={16} className="text-fc-red" />
                      <p className="text-[10px] tracking-[0.22em] uppercase font-bold text-fc-red">Credentials</p>
                    </div>
                    <p className="text-white/75 text-sm leading-relaxed">{instructor.credentials?.join(" · ")}</p>
                  </div>
                </div>
              </div>

              <div>
                <h2 className="fc-h-section text-2xl sm:text-3xl mb-8">
                  Courses by <span className="text-fc-red">{instructor.name.split(" ")[0]}</span>
                </h2>
                {courses.length === 0 ? (
                  <p className="text-white/50">No published courses yet.</p>
                ) : (
                  <div className="grid gap-6" style={{ gridTemplateColumns: "repeat(auto-fill, minmax(280px, 1fr))" }}>
                    {[...courses].sort((a, b) => Number(!!a.is_coming_soon) - Number(!!b.is_coming_soon)).map((c) => (
                      <CourseCard key={c.id} course={c} />
                    ))}
                  </div>
                )}
              </div>
            </>
          )}
        </section>
      </main>
      <Footer />
    </div>
  );
}
